import { convertToNumberIfPureNumber } from '../../../../core/utils/string-utils.js';

/**
 * Converts the value to a number if it is not one already and if it is possible.
 * If the converted value is a number and exceeds MAX_SAFE_INTEGER or if it is smaller than MIN_SAFE_INTEGER,
 * the value is converted to bigint.
 */
function convertToSafeNumber(value: string | number): string | number | bigint {
  const convertedValue =
    typeof value === 'number' ? value : convertToNumberIfPureNumber(value);

  const isValueBigInt =
    value !== Infinity &&
    value !== -Infinity &&
    typeof convertedValue === 'number' &&
    (convertedValue > Number.MAX_SAFE_INTEGER ||
      convertedValue < Number.MIN_SAFE_INTEGER);

  return isValueBigInt ? BigInt(value) : convertedValue;
}

/**
 * Compares number values in columns.
 */
export function numberlikeCompare(
  valueA: number | string | bigint,
  valueB: number | string | bigint,
): number {
  let aValue = valueA;
  let bValue = valueB;
  if (typeof valueA === 'string' || typeof valueA === 'number') {
    aValue = convertToSafeNumber(valueA);
  }

  if (typeof valueB === 'string' || typeof valueB === 'number') {
    bValue = convertToSafeNumber(valueB);
  }

  if (
    (typeof aValue === 'number' || typeof aValue === 'bigint') &&
    (typeof bValue === 'number' || typeof bValue === 'bigint')
  ) {
    return compareBasic(aValue, bValue);
  }
  return alphanumericCompare(
    toString(aValue).toLowerCase(),
    toString(bValue).toLowerCase(),
  );
}

/**
 * Compares alphanumeric values in columns.
 */
export function alphanumericCompare(valueA: string, valueB: string): number {
  const reSplitAlphaNumeric = /([0-9]+)/gm;
  // Split on number groups, but keep the delimiter
  // Then remove falsy values

  const aValue = valueA.split(reSplitAlphaNumeric).filter(Boolean);
  const bValue = valueB.split(reSplitAlphaNumeric).filter(Boolean);

  while (aValue.length && bValue.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const aValueString = aValue.shift()!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const bValueString = bValue.shift()!;

    const aValueInt = parseInt(aValueString, 10);
    const bValueInt = parseInt(bValueString, 10);

    const combo = [aValueInt, bValueInt].sort();

    // Both are string
    if (isNaN(combo[0])) {
      if (aValueString > bValueString) {
        return 1;
      }
      if (bValueString > aValueString) {
        return -1;
      }
      continue;
    }

    // One is a string, one is a number
    if (isNaN(combo[1])) {
      return isNaN(aValueInt) ? -1 : 1;
    }

    // Both are numbers
    if (aValueInt > bValueInt) {
      return 1;
    }
    if (bValueInt > aValueInt) {
      return -1;
    }
  }

  return aValue.length - bValue.length;
}

/**
 * Compares string values in columns.
 */
export function stringlikeCompare(valueA: string, valueB: string): number {
  return compareBasic(
    toString(valueA).toLowerCase(),
    toString(valueB).toLowerCase(),
  );
}

/**
 * Compares case-sensitive string values in columns.
 */
export function stringlikeCompareCaseSensitive(
  valueA: string,
  valueB: string,
): number {
  return compareBasic(valueA, valueB);
}

/**
 * Compares datetime values in columns.
 */
export function datelikeCompare(
  valueA: number | string | DateConstructor,
  valueB: number | string | DateConstructor,
): number {
  let aValue = valueA;
  let bValue = valueB;

  if (valueA instanceof Date) {
    aValue = valueA.getTime();
  }

  if (valueB instanceof Date) {
    bValue = valueB.getTime();
  }

  if (typeof valueA === 'string') {
    aValue = new Date(valueA).getTime();
  }

  if (typeof valueB === 'string') {
    bValue = new Date(valueB).getTime();
  }

  return alphanumericCompare(toString(aValue), toString(bValue));
}

/**
 *  Basic sorting used for comparing strings. Much faster then alphanumeric comparison.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function compareBasic(valueA: any, valueB: any): number {
  return valueA === valueB ? 0 : valueA > valueB ? 1 : -1;
}

function toString(value: unknown) {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
}
